// src/pages/trading-dashboard/components/MarketTicker.jsx
import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';

const MarketTicker = ({ markets, selectedMarket, onMarketSelect }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const getOutcomeColor = (price) => {
    if (price > 0.7) return 'text-success';
    if (price > 0.5) return 'text-warning-400';
    return 'text-error';
  };

  const getMarketStatusIcon = (status) => {
    switch (status) {
      case 'active': return 'Play';
      case 'closed': return 'Square';
      case 'resolved': return 'CheckCircle';
      default: return 'Clock';
    }
  };

  const getMarketStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-success';
      case 'closed': return 'text-warning-400';
      case 'resolved': return 'text-primary';
      default: return 'text-text-secondary';
    }
  };

  const formatVolume = (volume) => {
    if (volume >= 1000000) {
      return `$${(volume / 1000000)?.toFixed(1)}M`;
    } else if (volume >= 1000) {
      return `$${(volume / 1000)?.toFixed(0)}K`;
    }
    return `$${volume}`;
  };

  const getDaysUntilEnd = (endDate) => {
    const end = new Date(endDate);
    const now = new Date();
    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'Ended';
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return '1 day';
    return `${diffDays} days`;
  };

  return (
    <div className="bg-surface border border-border rounded-trading shadow-trading-card p-4 backdrop-blur-sm">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-heading-semibold text-text-primary">Market Overview</h2>
        <div className="flex items-center space-x-2">
          <div className="flex items-center space-x-1">
            <div className="w-2 h-2 bg-success rounded-full glow-success"></div>
            <span className="text-xs text-text-secondary">Live</span>
          </div>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 rounded hover:bg-surface-hover transition-colors duration-trading-fast">
            <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-text-secondary" />
          </button>
        </div>
      </div>
      {/* Selected Market Display */}
      {selectedMarket && (
        <div className="mb-4 p-4 bg-surface-hover rounded-trading border border-border">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3 flex-1">
              <div className="w-8 h-8 bg-gradient-primary rounded-full flex items-center justify-center glow-primary">
                <Icon name="TrendingUp" size={16} className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-body-semibold text-text-primary leading-tight mb-1">
                  {selectedMarket?.title}
                </h3>
                <p className="text-xs text-text-secondary mb-2 line-clamp-2">
                  {selectedMarket?.description}
                </p>
                <div className="flex items-center space-x-4">
                  <span className="text-xs bg-primary-900 text-primary-300 px-2 py-0.5 rounded">
                    {selectedMarket?.category}
                  </span>
                  <span className="text-xs text-text-secondary flex items-center">
                    <Icon name="Clock" size={12} className="mr-1" />
                    {getDaysUntilEnd(selectedMarket?.endDate)}
                  </span>
                </div>
              </div>
            </div>
            <div className="text-right ml-4">
              <div className="grid grid-cols-2 gap-2 mb-2">
                {selectedMarket?.outcomeTokens?.map((token, index) => (
                  <div key={index} className="text-center">
                    <p className="text-xs text-text-secondary">{token?.name}</p>
                    <p className={`font-data text-lg ${getOutcomeColor(token?.price)}`}>
                      ${token?.price?.toFixed(2)}
                    </p>
                  </div>
                ))}
              </div>
              <p className="text-xs text-text-secondary">
                Vol: {formatVolume(selectedMarket?.totalVolume)}
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Markets List */}
      {isExpanded && (
        <div className="space-y-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-body-medium text-text-secondary">Popular Markets</span>
            <button
              onClick={() => setIsLoading(!isLoading)}
              disabled={isLoading}
              className="p-1 rounded hover:bg-surface-hover transition-colors duration-trading-fast">
              <Icon
                name="RefreshCw"
                size={14}
                className={`text-text-secondary ${isLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
          
          {markets?.map((market) => (
            <button
              key={market?.id}
              onClick={() => onMarketSelect(market)}
              className={`w-full flex items-center justify-between p-3 rounded-trading transition-all duration-trading-fast ${
                selectedMarket?.id === market?.id 
                  ? 'bg-primary-900 border border-primary-800 text-primary-300 glow-primary' :'hover:bg-surface-hover border border-transparent hover:border-border-hover'
              }`}
            >
              <div className="flex items-center space-x-3 flex-1 min-w-0">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                  selectedMarket?.id === market?.id ? 'bg-primary glow-primary' : 'bg-border'
                }`}>
                  <Icon 
                    name={getMarketStatusIcon(market?.status)} 
                    size={12} 
                    className={selectedMarket?.id === market?.id ? 'text-white' : getMarketStatusColor(market?.status)} 
                  />
                </div>
                <div className="text-left flex-1 min-w-0">
                  <p className="font-body-medium text-sm text-text-primary truncate">
                    {market?.title}
                  </p>
                  <div className="flex items-center space-x-2">
                    <p className="text-xs text-text-secondary">{market?.category}</p>
                    <span className="text-xs text-text-secondary">•</span>
                    <p className="text-xs text-text-secondary">
                      {formatVolume(market?.totalVolume)}
                    </p>
                  </div>
                </div>
              </div>
              <div className="text-right ml-2">
                <div className="flex space-x-2">
                  {market?.outcomeTokens?.slice(0, 2)?.map((token, index) => (
                    <div key={index} className="text-center">
                      <p className="text-xs text-text-secondary">{token?.name}</p>
                      <p className={`font-data text-sm ${getOutcomeColor(token?.price)}`}>
                        ${token?.price?.toFixed(2)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MarketTicker;